import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, LogIn, Crown, Clock, ArrowLeft, Gamepad2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdModal from "@/components/AdModal";
import BottomNav from "@/components/BottomNav";
import { sounds } from "@/lib/sounds";

export default function Friends() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subStatus, setSubStatus] = useState(null);
  const [myRooms, setMyRooms] = useState([]);
  const [joinCode, setJoinCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [adOpen, setAdOpen] = useState(false);

  const isGuestUser = !!user?.is_guest;

  const handleGoogleLogin = () => {
    sounds.play("click");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  useEffect(() => {
    document.title = "Play with Friends — Private Chrome Dino Rooms | DareDino";
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
        // Guests can't create/join rooms — skip the sub/room fetches so
        // the gate renders immediately without a noisy 401/403.
        if (me.data?.is_guest) {
          setLoading(false);
          return;
        }
        const [sub, rooms] = await Promise.all([
          api.get("/subscription/status"),
          api.get("/rooms/my/active"),
        ]);
        setSubStatus(sub.data);
        setMyRooms(rooms.data.rooms || []);
      } catch {
        navigate("/", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const refreshStatus = async () => {
    try {
      const [sub, rooms] = await Promise.all([
        api.get("/subscription/status"),
        api.get("/rooms/my/active"),
      ]);
      setSubStatus(sub.data);
      setMyRooms(rooms.data.rooms || []);
    } catch {}
  };

  const actuallyCreateRoom = async ({ adWatched }) => {
    setCreating(true);
    try {
      const res = await api.post("/rooms/create", { ad_watched: adWatched });
      const code = res.data.room.room_code;
      sounds.play("cheer");
      toast.success(`Room ${code} created!`, {
        style: { background: "#0B0C10", border: "1px solid #EAEB18", color: "#EAEB18" },
      });
      navigate(`/room/${code}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Failed to create room";
      if (status === 402) {
        toast.error("Free tier requires an ad. Try again.", {
          style: { background: "#0B0C10", border: "1px solid #FF007F", color: "#FF007F" },
        });
      } else if (status === 403) {
        toast.error(detail, {
          style: { background: "#0B0C10", border: "1px solid #FF007F", color: "#FF007F" },
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setCreating(false);
      refreshStatus();
    }
  };

  const handleCreateClick = async () => {
    sounds.play("click");
    if (!subStatus) return;
    if (subStatus.is_pro) {
      // Pro: skip ad
      await actuallyCreateRoom({ adWatched: false });
      return;
    }
    if ((subStatus.remaining ?? 0) <= 0) {
      toast.error("Daily 3-room limit reached. Resets at midnight IST or upgrade to Pro.", {
        style: { background: "#0B0C10", border: "1px solid #FF007F", color: "#FF007F" },
      });
      return;
    }
    setAdOpen(true);
  };

  const handleAdComplete = () => {
    setAdOpen(false);
    actuallyCreateRoom({ adWatched: true });
  };

  const handleJoin = async (e) => {
    e?.preventDefault?.();
    sounds.play("click");
    const code = joinCode.trim().toUpperCase();
    if (code.length !== 6) {
      toast.error("Room code must be 6 characters");
      return;
    }
    setJoining(true);
    try {
      await api.post(`/rooms/${code}/join`);
      toast.success(`Joined room ${code}!`, {
        style: { background: "#0B0C10", border: "1px solid #00F0FF", color: "#00F0FF" },
      });
      navigate(`/room/${code}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not join room";
      toast.error(detail);
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-[#00F0FF] font-mono-arcade text-xs uppercase tracking-[0.3em] animate-pulse">
          Loading...
        </div>
      </div>
    );
  }

  // Guest gate — private rooms require a real (Google) identity so scores
  // and room ownership are stable. Show a friendly upgrade prompt instead
  // of silently redirecting to the landing page.
  if (isGuestUser) {
    return (
      <div className="relative min-h-screen bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />
        <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#00F0FF]/20">
          <button
            onClick={() => navigate("/game")}
            className="text-[#94A3B8] hover:text-white"
            data-testid="friends-back-btn"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="font-heading font-black text-lg text-white tracking-wider">
            DARE<span className="text-[#EAEB18]">DINO</span> <span className="text-[#00F0FF]">/ FRIENDS</span>
          </div>
        </header>
        <main
          data-testid="friends-guest-gate"
          className="relative z-10 max-w-2xl mx-auto px-6 md:px-12 py-16 text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-[#EAEB18] bg-[#EAEB18]/10 mb-6">
            <Lock size={28} strokeWidth={2.5} className="text-[#EAEB18]" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#EAEB18] mb-3">
            [ Google Sign-in Required ]
          </div>
          <h1
            data-testid="friends-guest-heading"
            className="font-heading font-black text-3xl md:text-5xl text-white mb-4"
          >
            Play With <span className="text-[#EAEB18]">Friends</span>
          </h1>
          <p className="text-sm md:text-base text-[#94A3B8] leading-relaxed mb-10 max-w-lg mx-auto">
            Private rooms need a real account so scores stay yours across
            devices and rooms don&rsquo;t get abandoned. Continue with Google
            to create or join a room — your guest score history stays with
            you and gets linked to your Google account.
          </p>
          <button
            data-testid="friends-guest-google-btn"
            onClick={handleGoogleLogin}
            className="btn-brutal px-8 py-4 text-sm md:text-base rounded-none inline-flex items-center gap-3"
          >
            <Gamepad2 size={20} strokeWidth={2.5} />
            Continue with Google to Play with Friends
          </button>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#050505] overflow-hidden">
      <div className="absolute inset-0 grid-overlay opacity-30 pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#00F0FF]/20">
        <button
          onClick={() => navigate("/game")}
          className="text-[#94A3B8] hover:text-white"
          data-testid="friends-back-btn"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="font-heading font-black text-lg text-white tracking-wider">
          DARE<span className="text-[#EAEB18]">DINO</span> <span className="text-[#00F0FF]">/ FRIENDS</span>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-12">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#00F0FF] mb-4">
          [ PRIVATE ROOMS ]
        </div>
        <h1 className="font-heading font-black text-3xl md:text-5xl text-white mb-3" data-testid="friends-title">
          Play With <span className="text-[#EAEB18]">Friends</span>
        </h1>
        <p className="text-sm text-[#94A3B8] max-w-2xl mb-10">
          Create a private room and share the code. Only players who join compete on that room&apos;s leaderboard.
        </p>

        {/* Plan status pill */}
        {subStatus && (
          <div
            className="inline-flex items-center gap-3 mb-8 border border-[#00F0FF]/40 bg-[#0B0C10] px-4 py-2"
            data-testid="friends-plan-status"
          >
            <span className="text-[10px] uppercase tracking-widest text-[#94A3B8]">Plan:</span>
            <span className={`text-xs font-mono uppercase font-bold ${subStatus.is_pro ? "text-[#EAEB18]" : "text-[#00F0FF]"}`}>
              {subStatus.is_pro ? "PRO · Unlimited" : `FREE · ${subStatus.remaining}/${subStatus.daily_limit} rooms left today`}
            </span>
            {!subStatus.is_pro && (
              <button
                onClick={() => navigate("/pricing")}
                className="ml-2 text-[10px] uppercase tracking-widest text-[#EAEB18] hover:underline"
                data-testid="friends-upgrade-link"
              >
                Upgrade →
              </button>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {/* CREATE ROOM */}
          <div className="border border-[#00F0FF]/30 bg-[#0B0C10] p-6 flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#EAEB18] flex items-center justify-center">
                <Plus size={20} strokeWidth={3} color="#050505" />
              </div>
              <h2 className="font-heading font-black text-xl text-white">Create Room</h2>
            </div>
            <p className="text-xs text-[#94A3B8] mb-6 flex-1">
              Get a private 6-character code. Share it with friends. Only players you invite can join.
            </p>
            <button
              onClick={handleCreateClick}
              disabled={creating}
              data-testid="create-room-btn"
              className="btn-brutal px-6 py-3 text-sm rounded-none flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Users size={18} strokeWidth={2.5} />
              {creating ? "Creating..." : subStatus?.is_pro ? "Create Room (Pro)" : "Watch Ad & Create"}
            </button>
          </div>

          {/* JOIN ROOM */}
          <div className="border border-[#00F0FF]/30 bg-[#0B0C10] p-6 flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#00F0FF] flex items-center justify-center">
                <LogIn size={20} strokeWidth={3} color="#050505" />
              </div>
              <h2 className="font-heading font-black text-xl text-white">Join Room</h2>
            </div>
            <p className="text-xs text-[#94A3B8] mb-6">
              Enter a 6-character room code your friend shared. No limit on how many rooms you can join.
            </p>
            <form onSubmit={handleJoin} className="flex flex-col gap-3 mt-auto">
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
                placeholder="ROOM CODE"
                maxLength={6}
                className="w-full bg-[#050505] border-2 border-[#00F0FF]/40 focus:border-[#00F0FF] px-4 py-3 text-center font-heading font-black text-2xl tracking-[0.5em] text-white outline-none"
                data-testid="join-room-code-input"
              />
              <button
                type="submit"
                disabled={joining || joinCode.length !== 6}
                data-testid="join-room-submit-btn"
                className="btn-ghost-cyan px-6 py-3 text-sm rounded-none disabled:opacity-40"
              >
                {joining ? "Joining..." : "Join"}
              </button>
            </form>
          </div>
        </div>

        {/* My active rooms */}
        {myRooms.length > 0 && (
          <div className="mb-10" data-testid="my-active-rooms">
            <h3 className="text-xs uppercase tracking-widest text-[#00F0FF] mb-3">Your Active Rooms</h3>
            <div className="space-y-2">
              {myRooms.map((r) => {
                const isCreator = r.creator_user_id === user?.user_id;
                const remainingMs = new Date(r.expires_at).getTime() - Date.now();
                const minsLeft = Math.max(0, Math.floor(remainingMs / 60000));
                return (
                  <button
                    key={r.room_id}
                    onClick={() => navigate(`/room/${r.room_code}`)}
                    data-testid={`active-room-${r.room_code}`}
                    className="w-full flex items-center justify-between border border-[#00F0FF]/25 bg-[#0B0C10] hover:border-[#EAEB18] px-4 py-3 text-left transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isCreator && <Crown size={16} className="text-[#EAEB18]" />}
                      <span className="font-heading font-black text-lg tracking-widest text-white">{r.room_code}</span>
                      <span className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
                        {isCreator ? "You created" : `by ${r.creator_name}`} · {r.players?.length || 1} player{(r.players?.length || 1) === 1 ? "" : "s"}
                      </span>
                    </div>
                    <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#00F0FF]">
                      <Clock size={12} /> {minsLeft}m left
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <AdModal
        open={adOpen}
        onComplete={handleAdComplete}
        onClose={() => setAdOpen(false)}
        reason="Watch a short ad to create your room"
      />
      <BottomNav />
    </div>
  );
}
